import { useEffect, useState } from "react";
import {
  AtSignIcon,
  BellIcon,
  BirdIcon,
  ChevronRightIcon,
  CircleDollarSignIcon,
  ChevronsUpDownIcon,
  FactoryIcon,
  FeatherIcon,
  LayoutDashboardIcon,
  LineChartIcon,
  LogOutIcon,
  MoonIcon,
  PanelLeftClose,
  PanelLeftOpen,
  PawPrint,
  SearchIcon,
  StarIcon,
  TrophyIcon,
  WrenchIcon,
} from "lucide-react";
import { Link, useLocation } from "react-router";

import { StafferPetIncentiveToken } from "../staffer-pets/StafferPetIncentiveToken";
import { StafferPetProfileSprite } from "../staffer-pets/StafferPetProfileSprite";
import { NotificationAlertMenuItem } from "./NotificationAlertMenuItem";
import { PinnedJobsSidebarMenuItem } from "./PinnedJobsSidebarMenuItem";
import { SidebarLogo } from "./SidebarLogo";
import { SidebarWeeklySubmits } from "./SidebarWeeklySubmits";
import { Column, Row, Text } from "@/components";
import { NotificationsSlideout } from "@/components/notifications/NotificationsSlideout";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useUnreadNotifications } from "@/contexts/UnreadNotificationsContext";
import {
  STAFFER_PET_OPEN_PLAYYARD_EVENT,
  STAFFER_PET_OPEN_SHOP_EVENT,
  STAFFER_PET_REWARD_EVENT,
  useStafferPet,
} from "@/hooks/useStafferPets";
import { useSignOut } from "@/hooks/mutations/useSignOut";
import { useGetOrphanedAtsJobs } from "@/hooks/queries/useGetOrphanedAtsJobs";
import { useUpsertPet } from "@/hooks/updateSets/useUpsertPet";
import {
  EARNINGS_PAGE_USER_IDS,
  MENTION_NOTIFICATION_TYPES,
  TOP_CANDIDATE_NOTIFICATION_TYPES,
  UNREAD_NOTIFICATIONS_DISPLAY_CAP,
} from "@/lib/constants";
import { getScreenFromPathname, mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { routes } from "@/lib/routes";
import { cn, isUserTyping } from "@/lib/utils";
import { appearanceStore } from "@/stores/appearance";
import { globalSearchStore } from "@/stores/globalSearch";
import {
  sidebarNavigationStore,
  ParentSidebarRoute,
} from "@/stores/sidebarNavigation";
import {
  NotificationFilterState,
  NotificationInboxFilter,
  NotificationMenuTone,
  PostSignInResponse,
  StafferPetCelebration,
  StafferPetPaletteId,
  UserRole,
} from "@/types";

// Animated glows pull focus in the always-visible sidebar.
const SIDEBAR_GLOWLESS_PALETTE_IDS: StafferPetPaletteId[] = ["PHX_RED"];

const UserWithCompanyAndAvatar = ({ user }: { user: PostSignInResponse }) => {
  // TODO: Show the company name here.
  return (
    <>
      <Row className="h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-200">
        <p className="text-[10px] font-medium text-slate-900">
          {user.firstName[0]}
          {user.lastName[0]}
        </p>
      </Row>
      <Column className="min-w-0 gap-[2px]">
        <p className="font-semibold leading-none">Laborup</p>
        <p className="truncate text-xs leading-none">{user.email}</p>
      </Column>
    </>
  );
};

// Read-only launcher for the ⌘K global search palette.
const SidebarSearchTrigger = () => {
  const openGlobalSearch = globalSearchStore((state) => state.openGlobalSearch);
  return (
    <Row className="relative min-w-0 flex-1">
      <SidebarInput
        className="cursor-pointer pl-8 pr-12"
        onClick={openGlobalSearch}
        placeholder="Search..."
        readOnly
      />
      <SearchIcon className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <kbd className="pointer-events-none absolute right-2 top-1/2 inline-flex h-5 -translate-y-1/2 items-center rounded border border-slate-200 bg-slate-50 px-1.5 font-mono text-[10px] font-medium text-slate-400">
        ⌘K
      </kbd>
    </Row>
  );
};

// Opens the notifications slideout, with an unread-count badge in the
// top-right corner.
const NotificationBellButton = ({
  className,
  onClick,
}: {
  className?: string;
  onClick: () => void;
}) => {
  const location = useLocation();
  const { unreadCount } = useUnreadNotifications();
  const badgeLabel =
    unreadCount > UNREAD_NOTIFICATIONS_DISPLAY_CAP
      ? `${UNREAD_NOTIFICATIONS_DISPLAY_CAP.toString()}+`
      : unreadCount.toString();

  return (
    <>
      <TooltipProvider delayDuration={500}>
        <Tooltip>
          <TooltipTrigger asChild>
            <SidebarMenuButton
              className={cn(
                "text-slate-500 hover:bg-slate-50 hover:text-slate-700 data-[active=true]:bg-transparent data-[active=true]:text-slate-900",
                className,
              )}
              isActive={routes.isNotificationsRouteActive(location.pathname)}
              onClick={onClick}
            >
              <BellIcon className="h-4 w-4" />
            </SidebarMenuButton>
          </TooltipTrigger>
          <TooltipContent side="right">
            <Row className="items-center gap-1.5">
              <Text className="text-xs font-medium text-slate-700">
                Notifications
              </Text>
              <kbd className="inline-flex h-4 items-center rounded border border-slate-200 bg-slate-50 px-1 font-mono text-[10px] font-medium text-slate-400">
                Ctrl N
              </kbd>
            </Row>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {unreadCount > 0 && (
        <Row className="pointer-events-none absolute -right-1 -top-1 z-10 h-4 min-w-4 items-center justify-center">
          <Text className="text-[10px] font-semibold leading-none text-primary-900">
            {badgeLabel}
          </Text>
        </Row>
      )}
    </>
  );
};

const SIDEBAR_PET_BOUNCE_DURATION_MS = 380;
// A placement bounces three times through the same keyframe (see index.css).
const SIDEBAR_PET_TRIPLE_BOUNCE_COUNT = 3;

// Each row opens the slideout scoped to its own types. Module level so their
// identity is stable across renders.
const topCandidatesSlideoutFilterState: NotificationFilterState = {
  categoryIds: [],
  inboxFilter: NotificationInboxFilter.UNREAD,
  searchQuery: "",
  types: TOP_CANDIDATE_NOTIFICATION_TYPES,
};
const unreadMentionsSlideoutFilterState: NotificationFilterState = {
  categoryIds: [],
  inboxFilter: NotificationInboxFilter.UNREAD,
  searchQuery: "",
  types: MENTION_NOTIFICATION_TYPES,
};

const sidebarNavMenuClassName =
  "gap-1.5 [&_[data-sidebar=menu-button]]:h-9 [&_[data-sidebar=menu-sub-button]]:h-8";
const sidebarNavGroupClassName = "px-2 pb-2.5 pt-1";
const sidebarNavGroupLabelClassName = "mb-1";

const parentSidebarRoutes: ParentSidebarRoute[] = [
  { label: "dashboard", path: "/" },
  { label: "job health", path: "/job-health" },
  { label: "companies", path: "/companies" },
  { label: "job tracker", path: "/tools/job-tracker" },
  { label: "placement tracker", path: "/placement-tracker" },
  { label: "my aviary", path: "/my-aviary" },
  { label: "leaderboard", path: "/leaderboard" },
  { label: "earnings", path: "/earnings" },
  { label: "notifications", path: "/notifications" },
];

export const AppSidebar = () => {
  const isDarkMode = appearanceStore((state) => state.isDarkMode);
  const toggleDarkMode = appearanceStore((state) => state.toggleDarkMode);
  const isWinterSidebar = appearanceStore((state) => state.isWinterSidebar);
  const setIsWinterSidebar = appearanceStore(
    (state) => state.setIsWinterSidebar,
  );
  const [toolsCollapsibleOpen, setToolsCollapsibleOpen] = useState(false);
  const [footerDropdownOpen, setFooterDropdownOpen] = useState(false);
  const [notificationsSlideoutOpen, setNotificationsSlideoutOpen] =
    useState(false);
  const [slideoutFilterState, setSlideoutFilterState] = useState<
    NotificationFilterState | undefined
  >(undefined);
  const [petBounce, setPetBounce] = useState<StafferPetCelebration | null>(
    null,
  );
  const location = useLocation();
  const setParentRoute = sidebarNavigationStore(
    (state) => state.setParentRoute,
  );

  const { open: sidebarOpen, setOpen: setSidebarOpen } = useSidebar();
  const { unreadCountsByType, unreadExcellentCandidateCount } =
    useUnreadNotifications();

  // If excellent candidates exist, only display that count for top candidates as it'll be purple
  const unreadTopCandidateCount = TOP_CANDIDATE_NOTIFICATION_TYPES.reduce(
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    (total, type) => total + (unreadCountsByType[type] || 0),
    0,
  );
  const hasExcellentCandidate = unreadExcellentCandidateCount > 0;
  const topCandidatesCount = hasExcellentCandidate
    ? unreadExcellentCandidateCount
    : unreadTopCandidateCount;

  // Anything waiting in the notifications section quiets the rest of the nav —
  // the section can only read as urgent if its neighbours are asleep.
  const hasUrgentNotifications = [
    ...TOP_CANDIDATE_NOTIFICATION_TYPES,
    ...MENTION_NOTIFICATION_TYPES,
  ].some((type) => Boolean(unreadCountsByType[type]));
  // Everything in the sidebar except the stud candidates section goes quiet, so the one
  // thing that needs attention is the only thing at full contrast.
  const dimmedWhenUrgentClassName = cn(
    "transition-opacity",
    hasUrgentNotifications && "opacity-75",
  );

  const handleNavClick = (destination: MIXPANEL_SCREENS) => {
    mixpanelUtils.trackNavClick(
      destination,
      getScreenFromPathname(location.pathname),
    );
  };
  const handleBellClick = () => {
    mixpanelUtils.trackButtonClick(
      "Notification bell",
      getScreenFromPathname(location.pathname),
      { is_opening: !notificationsSlideoutOpen },
    );
    setSlideoutFilterState(undefined);
    setNotificationsSlideoutOpen(!notificationsSlideoutOpen);
  };
  const handleTopCandidatesClick = () => {
    mixpanelUtils.trackButtonClick(
      "Top candidates",
      getScreenFromPathname(location.pathname),
    );
    setSlideoutFilterState(topCandidatesSlideoutFilterState);
    setNotificationsSlideoutOpen(true);
  };
  const handleUnreadMentionsClick = () => {
    mixpanelUtils.trackButtonClick(
      "Unread mentions",
      getScreenFromPathname(location.pathname),
    );
    setSlideoutFilterState(unreadMentionsSlideoutFilterState);
    setNotificationsSlideoutOpen(true);
  };
  // Ctrl+N toggles the notifications slideout (⌘N is reserved by browsers).
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key.toLowerCase() === "n" && !isUserTyping()) {
        e.preventDefault();
        mixpanelUtils.trackKeyboardShortcutUsed(
          "Ctrl+N",
          MIXPANEL_SCREENS.NOTIFICATIONS,
        );
        setSlideoutFilterState(undefined);
        setNotificationsSlideoutOpen((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const { mutate: signOut } = useSignOut();
  const { mutate: upsertPet } = useUpsertPet();
  const { data: orphanedAtsJobs, status: orphanedAtsJobsStatus } =
    useGetOrphanedAtsJobs();
  const hasOrphanedJobs =
    orphanedAtsJobsStatus === "success" && orphanedAtsJobs.length > 0;
  const { currentUser: user, currentUserEntry, pet } = useStafferPet();
  const isCurrentUserAStaffer =
    currentUserEntry?.userRole === UserRole.STAFFER &&
    currentUserEntry.deletedAt === null;
  // Shows every Staffer's assistant conversations; guac enforces the same.
  const canViewAssistantMonitor =
    currentUserEntry?.userRole === UserRole.ADMIN ||
    currentUserEntry?.userRole === UserRole.PRODUCT;

  useEffect(() => {
    const parentRoute = parentSidebarRoutes.find(
      (route) => route.path === location.pathname,
    );
    if (!parentRoute) return;
    setParentRoute(parentRoute);
  }, [location.pathname, setParentRoute]);

  useEffect(() => {
    const handlePetReward = (event: Event) => {
      const rewardEvent = event as CustomEvent<StafferPetCelebration>;
      setPetBounce(rewardEvent.detail);
    };

    window.addEventListener(STAFFER_PET_REWARD_EVENT, handlePetReward);
    return () => {
      window.removeEventListener(STAFFER_PET_REWARD_EVENT, handlePetReward);
    };
  }, []);

  useEffect(() => {
    if (petBounce === null) return;

    const durationMs =
      petBounce === "triple"
        ? SIDEBAR_PET_BOUNCE_DURATION_MS * SIDEBAR_PET_TRIPLE_BOUNCE_COUNT
        : SIDEBAR_PET_BOUNCE_DURATION_MS;
    const bounceTimeout = window.setTimeout(() => {
      setPetBounce(null);
    }, durationMs);

    return () => {
      window.clearTimeout(bounceTimeout);
    };
  }, [petBounce]);

  if (!user) {
    return null;
  }

  const handleSignOut = () => {
    signOut();
  };

  const handleSidebarThemeChange = (value: string) => {
    setIsWinterSidebar(value === "winter");
  };

  const handlePetClick = () => {
    setPetBounce("single");
    mixpanelUtils.trackButtonClick(
      "Bounce pet",
      getScreenFromPathname(location.pathname),
    );
    // Let the hop land before the playyard dialog covers the sidebar.
    window.setTimeout(() => {
      window.dispatchEvent(new Event(STAFFER_PET_OPEN_PLAYYARD_EVENT));
    }, SIDEBAR_PET_BOUNCE_DURATION_MS);
  };

  const handleTogglePetVisibility = () => {
    if (pet === null) return;
    upsertPet({
      userId: user.id,
      pet: { ...pet, shouldHide: !pet.shouldHide },
    });
  };

  const handleIncentiveTokenClick = () => {
    mixpanelUtils.trackButtonClick(
      "Open incentive shop",
      getScreenFromPathname(location.pathname),
    );
    window.dispatchEvent(
      new CustomEvent(STAFFER_PET_OPEN_SHOP_EVENT, { detail: "INCENTIVES" }),
    );
  };

  return (
    <>
      {/* Rendered before the sidebar and at a lower z-index so it slides out
          from underneath it. */}
      <NotificationsSlideout
        initialFilterState={slideoutFilterState}
        open={notificationsSlideoutOpen}
        setOpen={setNotificationsSlideoutOpen}
      />
      <Sidebar
        className={cn("z-40", isWinterSidebar && "winter-sidebar-frame")}
        collapsible="icon"
      >
        {/* Header */}
        <SidebarHeader
          className={cn(
            "gap-2 px-3 pb-2.5 pt-6",
            isWinterSidebar && "winter-sidebar-header",
            dimmedWhenUrgentClassName,
          )}
        >
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                // TODO: This is a hack to ensure that the Laborup blue gear icon
                // is centered. There is probably a better way to do it but it's
                // also referenced here:
                // https://ui.shadcn.com/docs/components/sidebar#styling
                className="group-data-[collapsible=icon]:!p-1"
              >
                <Link to="">
                  <SidebarLogo isWinter={isWinterSidebar} />
                </Link>
              </SidebarMenuButton>
              <SidebarMenuAction
                onClick={() => {
                  setSidebarOpen(false);
                }}
              >
                <PanelLeftClose />
              </SidebarMenuAction>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        {/* Content */}
        <SidebarContent className="gap-3">
          {/* Search + notifications — stays pinned above the scrolling nav */}
          <SidebarGroup
            className={cn(
              "shrink-0 px-2 pb-0",
              sidebarOpen ? "pt-2" : "pt-2.5",
              dimmedWhenUrgentClassName,
            )}
          >
            <SidebarGroupContent>
              {sidebarOpen ? (
                <Row className="items-center gap-1.5">
                  <SidebarSearchTrigger />
                  <SidebarMenu className="w-auto shrink-0">
                    <SidebarMenuItem>
                      <NotificationBellButton
                        className="h-8 w-8"
                        onClick={handleBellClick}
                      />
                    </SidebarMenuItem>
                  </SidebarMenu>
                </Row>
              ) : (
                <SidebarMenu>
                  <SidebarMenuItem>
                    <NotificationBellButton onClick={handleBellClick} />
                  </SidebarMenuItem>
                </SidebarMenu>
              )}
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Scrollable nav — logo/search stay pinned above, footer below */}
          <Column className="min-h-0 flex-1 gap-3 overflow-y-auto overflow-x-hidden pb-2">
            {/* Notifications — only renders when something is waiting */}
            {hasUrgentNotifications ? (
              <>
                <SidebarGroup className={sidebarNavGroupClassName}>
                  <SidebarGroupLabel className={sidebarNavGroupLabelClassName}>
                    Notifications
                  </SidebarGroupLabel>
                  <SidebarGroupContent>
                    <SidebarMenu className={sidebarNavMenuClassName}>
                      <NotificationAlertMenuItem
                        count={topCandidatesCount}
                        Icon={StarIcon}
                        label="Top candidates"
                        onClick={handleTopCandidatesClick}
                        tone={
                          hasExcellentCandidate
                            ? NotificationMenuTone.EXCELLENT
                            : NotificationMenuTone.GREAT
                        }
                        types={TOP_CANDIDATE_NOTIFICATION_TYPES}
                      />
                      <NotificationAlertMenuItem
                        Icon={AtSignIcon}
                        label="Unread mentions"
                        onClick={handleUnreadMentionsClick}
                        tone={NotificationMenuTone.MENTION}
                        types={MENTION_NOTIFICATION_TYPES}
                      />
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>

                <SidebarSeparator className="my-1" />
              </>
            ) : null}

            {/* Staffer */}
            <SidebarGroup
              className={cn(
                sidebarNavGroupClassName,
                dimmedWhenUrgentClassName,
              )}
            >
              <SidebarGroupLabel className={sidebarNavGroupLabelClassName}>
                Staffer
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu className={sidebarNavMenuClassName}>
                  {!sidebarOpen && ( // TODO: See if there is a better way to do this.
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        onClick={() => {
                          setSidebarOpen(true);
                        }}
                        tooltip="Open"
                      >
                        <PanelLeftOpen />
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={routes.isDashboardRouteActive(
                        location.pathname,
                      )}
                      tooltip="Dashboard"
                      asChild
                    >
                      <Link
                        to="/"
                        onClick={() => {
                          handleNavClick(MIXPANEL_SCREENS.DASHBOARD);
                        }}
                      >
                        <LayoutDashboardIcon />
                        <span className="text-base font-medium">Dashboard</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={routes.isMyAviaryRouteActive(location.pathname)}
                      tooltip="Aviary"
                      asChild
                    >
                      <Link
                        to="my-aviary"
                        onClick={() => {
                          handleNavClick(MIXPANEL_SCREENS.MY_AVIARY);
                        }}
                      >
                        <FeatherIcon />
                        <span className="text-base font-medium">Aviary</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={routes.isLeaderboardRouteActive(
                        location.pathname,
                      )}
                      tooltip="Leaderboard"
                      asChild
                    >
                      <Link
                        to="leaderboard"
                        onClick={() => {
                          handleNavClick(MIXPANEL_SCREENS.LEADERBOARD);
                        }}
                      >
                        <TrophyIcon />
                        <span className="text-base font-medium">
                          Leaderboard
                        </span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  {/* Early-access rollout while earnings data accuracy is being verified */}
                  {EARNINGS_PAGE_USER_IDS.includes(user.id) ? (
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        isActive={routes.isEarningsRouteActive(
                          location.pathname,
                        )}
                        tooltip="Earnings"
                        asChild
                      >
                        <Link
                          to="earnings"
                          onClick={() => {
                            handleNavClick(MIXPANEL_SCREENS.EARNINGS);
                          }}
                        >
                          <CircleDollarSignIcon />
                          <span className="text-base font-medium">
                            Earnings
                          </span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ) : null}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarSeparator
              className={cn("my-1", dimmedWhenUrgentClassName)}
            />

            {/* Admin */}
            <SidebarGroup
              className={cn(
                sidebarNavGroupClassName,
                dimmedWhenUrgentClassName,
              )}
            >
              <SidebarGroupLabel className={sidebarNavGroupLabelClassName}>
                Admin
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu className={sidebarNavMenuClassName}>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={routes.isJobHealthRouteActive(
                        location.pathname,
                      )}
                      tooltip="Job Health"
                      asChild
                    >
                      <Link
                        to="job-health"
                        onClick={() => {
                          handleNavClick(MIXPANEL_SCREENS.JOB_HEALH);
                        }}
                      >
                        <LineChartIcon />
                        <span className="text-base font-medium">
                          Job Health
                        </span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={routes.isCompaniesRouteActive(
                        location.pathname,
                      )}
                      tooltip="Companies"
                      asChild
                    >
                      <Link
                        to="/companies"
                        onClick={() => {
                          handleNavClick(MIXPANEL_SCREENS.COMPANIES);
                        }}
                      >
                        <FactoryIcon />
                        <span className="text-base font-medium">Companies</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={routes.isPlacementTrackerRouteActive(
                        location.pathname,
                      )}
                      tooltip="Placements"
                      asChild
                    >
                      <Link
                        to="placement-tracker"
                        onClick={() => {
                          handleNavClick(MIXPANEL_SCREENS.PLACEMENT_TRACKER);
                        }}
                      >
                        <BirdIcon />
                        <span className="text-base font-medium">
                          Placements
                        </span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <Collapsible
                    className="group/collapsible"
                    onOpenChange={(open) => {
                      if (!sidebarOpen) {
                        // TODO: See if there is a better way to do this.
                        // If the sidebar is in the process of opening, ensure
                        // that the tools collapsible will be open.
                        setToolsCollapsibleOpen(true);
                      } else {
                        setToolsCollapsibleOpen(open);
                      }
                    }}
                    open={toolsCollapsibleOpen}
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton
                          isActive={routes.isToolsRouteActive(
                            location.pathname,
                          )}
                          onClick={() => {
                            // If the sidebar is closed when the tools collapsible
                            // is clicked, open the sidebar.
                            if (!sidebarOpen) {
                              setSidebarOpen(true);
                            }
                          }}
                          tooltip="Tools"
                        >
                          <WrenchIcon />
                          <span className="text-base font-medium">Tools</span>
                          <ChevronRightIcon className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {canViewAssistantMonitor && (
                            <SidebarMenuSubItem>
                              <SidebarMenuSubButton
                                asChild
                                isActive={routes.isToolsAssistantRouteActive(
                                  location.pathname,
                                )}
                              >
                                <Link to="tools/assistant">
                                  <span>Assistant</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          )}
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={routes.isToolsCampaignsRouteActive(
                                location.pathname,
                              )}
                            >
                              <Link
                                to="tools/campaigns"
                                onClick={() => {
                                  handleNavClick(MIXPANEL_SCREENS.CAMPAIGNS);
                                }}
                              >
                                <span>Campaigns</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={routes.isToolsDemandRouteActive(
                                location.pathname,
                              )}
                            >
                              <Link
                                to="tools/demand"
                                onClick={() => {
                                  handleNavClick(MIXPANEL_SCREENS.DEMAND);
                                }}
                              >
                                <span>Demand</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={routes.isToolsDemoConfigsRouteActive(
                                location.pathname,
                              )}
                            >
                              <Link
                                to="tools/demo-configs"
                                onClick={() => {
                                  handleNavClick(MIXPANEL_SCREENS.DEMO_CONFIGS);
                                }}
                              >
                                <span>Demo Configs</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={routes.isToolsExploreImagesRouteActive(
                                location.pathname,
                              )}
                            >
                              <Link
                                to="tools/explore-images"
                                onClick={() => {
                                  handleNavClick(
                                    MIXPANEL_SCREENS.EXPLORE_IMAGES,
                                  );
                                }}
                              >
                                <span>Explore Images</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={routes.isToolsSadieDashboardRouteActive(
                                location.pathname,
                              )}
                            >
                              <Link
                                to="tools/sadie-dashboard"
                                onClick={() => {
                                  handleNavClick(
                                    MIXPANEL_SCREENS.SADIE_DASHBOARD,
                                  );
                                }}
                              >
                                <span>Sadie Dashboard</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={routes.isToolsSadieSimulatorRouteActive(
                                location.pathname,
                              )}
                            >
                              <Link
                                to="tools/sadie-simulator"
                                onClick={() => {
                                  handleNavClick(
                                    MIXPANEL_SCREENS.SADIE_SIMULATOR,
                                  );
                                }}
                              >
                                <span>Sadie Simulator</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={routes.isToolsJobTrackerRouteActive(
                                location.pathname,
                              )}
                            >
                              <Link
                                to="tools/job-tracker"
                                onClick={() => {
                                  handleNavClick(MIXPANEL_SCREENS.JOB_TRACKER);
                                }}
                              >
                                <span>Job Tracker</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={routes.isToolsMetricsRouteActive(
                                location.pathname,
                              )}
                            >
                              <Link
                                to="tools/metrics"
                                onClick={() => {
                                  handleNavClick(MIXPANEL_SCREENS.METRICS);
                                }}
                              >
                                <span>Metrics</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={routes.isToolsOrphanedWorkableJobsRouteActive(
                                location.pathname,
                              )}
                            >
                              <Link
                                to="tools/orphaned-workable-jobs"
                                onClick={() => {
                                  handleNavClick(
                                    MIXPANEL_SCREENS.ORPHANED_WORKABLE_JOBS,
                                  );
                                }}
                              >
                                <span
                                  className={cn(
                                    hasOrphanedJobs &&
                                      "font-semibold text-red-500",
                                  )}
                                >
                                  Orphaned Workable Jobs
                                </span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={routes.isToolsSignupVariantsRouteActive(
                                location.pathname,
                              )}
                            >
                              <Link
                                to="tools/signup-variants"
                                onClick={() => {
                                  handleNavClick(
                                    MIXPANEL_SCREENS.SIGNUP_VARIANTS,
                                  );
                                }}
                              >
                                <span>Signup Variants</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={routes.isToolsUsersRouteActive(
                                location.pathname,
                              )}
                            >
                              <Link
                                to="tools/users"
                                onClick={() => {
                                  handleNavClick(MIXPANEL_SCREENS.USERS);
                                }}
                              >
                                <span>Users</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={routes.isToolsVoiceRecordingsRouteActive(
                                location.pathname,
                              )}
                            >
                              <Link
                                to="tools/voice-recordings"
                                onClick={() => {
                                  handleNavClick(MIXPANEL_SCREENS.CAMPAIGNS);
                                }}
                              >
                                <span>Voice Recordings</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarSeparator className={dimmedWhenUrgentClassName} />
            <PinnedJobsSidebarMenuItem className={dimmedWhenUrgentClassName} />
          </Column>
        </SidebarContent>

        {/* Footer */}
        <SidebarFooter className={dimmedWhenUrgentClassName}>
          <Column className="relative gap-2 px-1 pb-1 group-data-[collapsible=icon]:hidden">
            <SidebarWeeklySubmits userId={user.id} />
            {pet !== null && !pet.shouldHide && (
              <Column
                // With goals, tuck into the card's bottom-right corner; without,
                // render in place where the card would be.
                className={cn(
                  "relative h-auto w-auto",
                  isCurrentUserAStaffer
                    ? "absolute -bottom-2 -right-1 z-10"
                    : "z-10 translate-y-6 self-end",
                )}
              >
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handlePetClick}
                  className="h-auto w-auto p-0 hover:!bg-transparent dark:hover:!bg-transparent"
                >
                  <StafferPetProfileSprite
                    pet={pet}
                    sizePx={72}
                    isBouncing={petBounce !== null}
                    isTripleBounce={petBounce === "triple"}
                    hasPaletteGlow={
                      !SIDEBAR_GLOWLESS_PALETTE_IDS.includes(
                        pet.equipped.colorId,
                      )
                    }
                  />
                </Button>
                {pet.incentiveTokensAvailable > 0 && (
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={handleIncentiveTokenClick}
                          className="absolute -left-2 -top-1 z-20 h-auto w-auto p-0 hover:bg-transparent"
                        >
                          <StafferPetIncentiveToken sizePx={24} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-52">
                        You have {pet.incentiveTokensAvailable} incentive token
                        {pet.incentiveTokensAvailable === 1 ? "" : "s"} to
                        redeem — click to visit the shop
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </Column>
            )}
          </Column>
          <SidebarMenu className="border-t border-slate-200">
            <SidebarMenuItem>
              <DropdownMenu
                onOpenChange={setFooterDropdownOpen}
                open={footerDropdownOpen}
              >
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground" // TODO: Figure out if necessary.
                    size="lg"
                  >
                    <UserWithCompanyAndAvatar user={user} />
                    <ChevronsUpDownIcon className="ml-auto" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  side={sidebarOpen ? "top" : "right"}
                  className={cn(
                    "w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-l",
                    isWinterSidebar && "winter-sidebar-profile-menu",
                  )}
                >
                  <DropdownMenuLabel>Sidebar theme</DropdownMenuLabel>
                  <DropdownMenuRadioGroup
                    onValueChange={handleSidebarThemeChange}
                    value={isWinterSidebar ? "winter" : "standard"}
                  >
                    <DropdownMenuRadioItem value="winter">
                      Winter is Coming
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="standard">
                      Standard
                    </DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={toggleDarkMode}>
                    <MoonIcon />
                    {isDarkMode ? "Disable dark mode" : "Enable dark mode"}
                  </DropdownMenuItem>
                  {pet !== null && (
                    <DropdownMenuItem onClick={handleTogglePetVisibility}>
                      <PawPrint />
                      {pet.shouldHide ? "Show Pet" : "Hide Pet"}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOutIcon />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
    </>
  );
};
